import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

export class PageEvent {
  pageIndex!: number;
  previousPageIndex?: number;
  pageSize!: number;
  length!: number;
}

export interface PaginatorDefaultOptions {
  pageSize: number;
  pageSizeOptions: number[];
}

const PAGINATOR_DEFAULT_OPTIONS: PaginatorDefaultOptions = {
  pageSize: 20,
  pageSizeOptions: [20, 50, 100]
};

class PageItem {
  constructor(public index?: number) { }
}

@Component({
  standalone: false,
  selector: 'app-paginator',
  templateUrl: './paginator.component.html',
  styleUrls: ['./paginator.component.scss']
})
export class PaginatorComponent implements OnInit {

  @Input()
  get length(): number {
    return this.lengthValue;
  }
  set length(value: number) {
    this.lengthValue = value;
    this.refreshPageItems();
  }
  private lengthValue: number = 0;

  @Input()
  get pageIndex(): number {
    return this.pageIndexValue;
  }
  set pageIndex(value: number) {
    this.pageIndexValue = value;
    this.refreshPageItems();
  }
  private pageIndexValue: number = 0;

  @Input()
  get pageSize(): number {
    return this.pageSizeValue;
  }
  set pageSize(value: number) {
    this.pageIndex = 0;
    this.pageSizeValue = value;
  }
  private pageSizeValue: number = PAGINATOR_DEFAULT_OPTIONS.pageSize;

  @Input()
  get pageSizeOptions(): number[] {
    return this.pageSizeOptionsValue;
  }
  set pageSizeOptions(value: number[]) {
    this.pageSizeOptionsValue = value;
  }
  private pageSizeOptionsValue: number[] = PAGINATOR_DEFAULT_OPTIONS.pageSizeOptions;

  @Output()
  page = new EventEmitter<PageEvent>();

  get from(): number {
    return Math.min(this.pageIndex * this.pageSize + 1, this.length);
  }

  get to(): number {
    return Math.min(this.pageIndex * this.pageSize + this.pageSize, this.length);
  }

  pageItems: PageItem[] = [];

  constructor() {
  }

  ngOnInit() {
    this.refreshPageItems();
  }

  onPageSizeSelect(event: any) {
    const pageSize = Number(event?.target?.value);
    if (isNaN(pageSize)) {
      return;
    }

    this.pageSize = pageSize;
    this.emitPageEvent();
  }

  get isFirstPage() {
    return this.pageIndex !== 0;
  }

  onFirstPageClick() {
    if (!this.isFirstPage) {
      return;
    }

    this.pageIndex = 0;
    this.emitPageEvent();
  }

  get lastPageIndex() {
    return Math.max(this.getNumberOfPages() - 1, 0);
  }

  get isLastPage() {
    return this.pageIndex !== this.lastPageIndex;
  }

  onLastPageClick() {
    if (!this.isLastPage) {
      return;
    }

    this.pageIndex = this.lastPageIndex;
    this.emitPageEvent();
  }

  onPageClick(pageIndex: number) {
    if (isNaN(Number(pageIndex)) || this.pageIndex === pageIndex) {
      return;
    }

    this.pageIndex = pageIndex;
    this.emitPageEvent();
  }

  private emitPageEvent() {
    this.page.emit({
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      length: this.length
    });
  }

  private getNumberOfPages() {
    return Math.ceil(this.length / this.pageSize);
  }

  private refreshPageItems() {
    this.pageItems = this.getPageItems();
  }

  private getPageItems(): PageItem[] {
    const maxElements = 7;
    const numberOfPages = this.getNumberOfPages();

    if (numberOfPages <= maxElements) {
      return this.createPageItems(0, numberOfPages - 1);
    }

    if (this.pageIndex <= 3) {
      return [
        ...this.createPageItems(0, 4),
        new PageItem(),
        new PageItem(numberOfPages - 1)
      ]
    }

    if (this.pageIndex >= numberOfPages - 4) {
      return [
        new PageItem(0),
        new PageItem(),
        ...this.createPageItems(numberOfPages - 5, numberOfPages - 1)
      ]
    }

    return [
      new PageItem(0),
      new PageItem(),
      ...this.createPageItems(this.pageIndex - 1, this.pageIndex + 1),
      new PageItem(),
      new PageItem(numberOfPages - 1)
    ]
  }

  private createPageItems(fromIndex: number, toIndex: number) {
    const pageItems = [];
    for (var i = fromIndex; i <= toIndex; i++) {
      pageItems.push(new PageItem(i));
    }

    return pageItems;
  }
}
